import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import LoadingSpinner from "../LoadingSpinner";

/**
 * Google Sign-In Button Component
 *
 * Handles Google OAuth flow for login and registration.
 * Uses popup-based flow with authorization code exchange.
 */
const GoogleSignInButton = ({
  onSuccess,
  onError,
  mode = "login", // 'login' | 'register' | 'link'
  disabled = false,
  className = "",
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      setIsLoading(true);
      try {
        // Pass the authorization code to parent for backend exchange
        await onSuccess({
          code: codeResponse.code,
          scope: codeResponse.scope,
        });
      } catch (error) {
        console.error("Google sign-in error:", error);
        onError?.(error.message || "Google sign-in failed");
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google OAuth error:", error);
      setIsLoading(false);

      // Map common errors to user-friendly messages
      const errorMessages = {
        access_denied: "You cancelled the sign-in. Please try again.",
        popup_closed_by_user: "Sign-in was cancelled. Please try again.",
        immediate_failed: "Could not sign in automatically. Please try again.",
      };

      const message =
        errorMessages[error?.error] ||
        error?.error_description ||
        "Google sign-in failed. Please try again.";

      onError?.(message);
    },
    onNonOAuthError: (error) => {
      console.error("Google non-OAuth error:", error);
      setIsLoading(false);
      onError?.("Failed to open Google sign-in. Please check your popup blocker.");
    },
  });

  const handleClick = () => {
    if (disabled || isLoading) return;
    googleLogin();
  };

  const buttonText = {
    login: "Continue with Google",
    register: "Sign up with Google",
    link: "Link Google Account",
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`
        w-full flex items-center justify-center gap-3
        px-4 py-2.5
        border border-gray-300 rounded-md
        bg-white hover:bg-gray-50
        text-gray-700 font-medium text-sm
        transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
        ${className}
      `}
    >
      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : (
        <>
          {/* Google Logo SVG */}
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>{buttonText[mode] || buttonText.login}</span>
        </>
      )}
    </button>
  );
};

export default GoogleSignInButton;
